// ============================================================================
// CANCUN100 — EquipoAgentes.js  (Contabilidad v3 · Bloque 52)
// ----------------------------------------------------------------------------
// Sección «Equipo · Agentes» de la pestaña 👥 Agentes: alta, pausa, reactivación, baja y reenvío de invitación en la APP
// y en KOMITO con un solo botón, por la Edge Function `alta-agente` (Claude). El navegador solo manda
// { accion, agente_id, motivo?, solicitud_id, solo? }: el equipo sale del agente y el `solicitante` lo pone el servidor
// con el correo del JWT. Aquí no hay llaves, URLs de la puerta ni SQL. Nunca se borra nada ni se tocan contraseñas.
// Permisos (los aplica el servidor; aquí solo se ocultan botones): super_admin = los dos equipos + baja;
// top_tours (Maggie) = solo Top Tours; contabilidad (Tere) = solo Sala. Lectura: agentes y altas_bitacora (RLS por rol).
// ============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom'; // B52: la tarjeta usa backdrop-filter, que encierra a los position:fixed
import { supabase } from './supabaseClient';
import { RADIO, RADIO_TARJETA, RADIO_INSIGNIA } from './tema';

export const ROLES_EQUIPO = ['super_admin', 'top_tours', 'contabilidad'];
export const equiposDeRol = (rol) => (rol === 'super_admin' ? ['top_tours', 'sala'] : rol === 'top_tours' ? ['top_tours'] : rol === 'contabilidad' ? ['sala'] : []);
export const NOMBRE_EQUIPO_B52 = { top_tours: 'Top Tours', sala: 'Sala de Ventas' };
export const CORREO_C100 = /^[a-z0-9._%+-]+@cancun100\.com$/;
export const correoC100 = (c) => CORREO_C100.test(String(c || '').trim().toLowerCase());
export const AVISO_DOS_CORREOS = 'Le llegan dos correos: uno de Komito y otro de la app. Crea dos contraseñas, una para cada uno.';
export const MSG_ACCESO_PAUSA = 'Tu acceso está en pausa. Habla con tu supervisor.';
export const MSG_ACCESO_BAJA = 'Tu acceso a la app fue cerrado.'; // Bloque 52-B
// Bloque 52-B: pausa y baja solo cierran el acceso. La etiqueta sale de pausa_at / baja_at; activo ya no se toca.
export const etiquetaAcceso = (a) => (!a ? null : a.baja_at ? 'Baja' : a.pausa_at ? 'Pausa' : null);
export const sinAcceso = (a) => !!a && (!!a.pausa_at || !!a.baja_at || a.activo === false);
export const mensajeSinAcceso = (a) => (a && a.baja_at ? MSG_ACCESO_BAJA : MSG_ACCESO_PAUSA);
const COLOR_BAJA = 'var(--au-text-faint)'; const COLOR_PAUSA = 'var(--au-orange-fg)'; // APP 2/20 Fase B: tokens
// Chip chico para las listas de admin (no oculta nada ni cambia filtros).
export const ChipAcceso = ({ a, style = null }) => {
  const e = etiquetaAcceso(a);
  if (!e) return null;
  const col = e === 'Baja' ? COLOR_BAJA : COLOR_PAUSA;
  const t = e === 'Baja' ? `Baja${a.baja_at ? ' · ' + String(a.baja_at).slice(0, 10) : ''}: sin acceso a la app` : `En pausa${a.pausa_at ? ' desde ' + String(a.pausa_at).slice(0, 10) : ''}: sin acceso a la app`;
  return <span title={t} style={{ display: 'inline-block', marginLeft: 6, padding: '0 6px', borderRadius: 5, fontSize: 12, fontWeight: 800, letterSpacing: '.04em', lineHeight: '15px', verticalAlign: 'middle', color: col, background: `color-mix(in srgb, ${col} 16%, transparent)`, border: `1px solid color-mix(in srgb, ${col} 45%, transparent)`, whiteSpace: 'nowrap', ...(style || {}) }}>{e === 'Baja' ? '⛔ Baja' : '⏸ Pausa'}</span>;
};
const PAUSA_REFRESCO_MS = 7000; // la puerta de Komito permite 10 llamadas por minuto POR ADMIN (lo de un admin no le quita espacio a otro)
export const EN_PROCESO_MS = 10000; // EN_PROCESO: a los 10 s se consulta solo a ese agente
export const MSG_EN_PROCESO = 'Komito sigue procesando; actualiza en unos segundos';
export const komitoEnProceso = (res) => !!res?.komito && !res.komito.ok && res.komito.error === 'EN_PROCESO';

// uuid v4 para solicitud_id (cada clic y cada «Reintentar» lleva uno nuevo; el reintento por tiempo lo hace el servidor)
export const nuevoSolicitudId = () => {
  try { if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID(); } catch (e) { /* sin crypto */ }
  const h = '0123456789abcdef'; let s = '';
  for (let i = 0; i < 36; i++) s += [8, 13, 18, 23].includes(i) ? '-' : i === 14 ? '4' : i === 19 ? h[8 + Math.floor(Math.random() * 4)] : h[Math.floor(Math.random() * 16)];
  return s;
};

// Llama a la Edge Function y siempre devuelve un objeto (los 4xx traen { ok:false, error, mensaje } en el cuerpo).
export const llamarAltaAgente = async ({ accion, agente_id, motivo = null, solo = null, solicitud_id = null }) => {
  const body = { accion, agente_id, solicitud_id: solicitud_id || nuevoSolicitudId(), ...(motivo ? { motivo } : {}), ...(solo ? { solo } : {}) };
  try {
    const { data, error } = await supabase.functions.invoke('alta-agente', { body });
    if (!error) return data || { ok: false, error: 'SIN_RESPUESTA', mensaje: 'La función no respondió.' };
    let det = null;
    try { if (error.context && typeof error.context.json === 'function') det = await error.context.json(); } catch (e) { det = null; }
    if (det && typeof det === 'object') return det;
    return { ok: false, error: 'RED', mensaje: error.message || 'No se pudo llamar a la función.' };
  } catch (e) {
    return { ok: false, error: 'RED', mensaje: String(e?.message || e) };
  }
};

// ── Textos ──
const MSG_KOMITO = {
  SIN_LLAVE: 'Falta configurar la llave de Komito (Marco).',
  LIMITE: 'Espera un minuto: Komito acepta 10 solicitudes por minuto por admin.',
  EN_PROCESO: MSG_EN_PROCESO,
  OTRO_EQUIPO: 'Está en el otro equipo; lo mueve Marco a mano.',
  TIMEOUT: 'Komito no respondió a tiempo (ya se reintentó una vez). Revisa en Komito antes de reintentar.',
  NO_EXISTE: 'No está en Komito.',
  YA_ACTIVO: 'Ya creó su contraseña en Komito; no hace falta reenviar.',
  NO_PERMITIDO: 'Komito no permite esta acción a tu usuario.',
  CORREO_INVALIDO: 'Komito rechazó el correo.',
};
const MSG_APP = {
  enviada: 'Invitación enviada', reenviada: 'Invitación reenviada', ya_tenia_contrasena: 'Ya tenía contraseña en la app: no se manda invitación',
  acceso_bloqueado: 'Acceso bloqueado', acceso_devuelto: 'Acceso devuelto', sin_cuenta_de_acceso: 'Todavía no tenía cuenta de acceso (no había nada que bloquear ni devolver)',
};
const PASO_LABEL = { usuario: 'Usuario', bandejas: 'Bandejas', equipo: 'Equipo', invitacion: 'Invitación', sesion: 'Sesión', conversaciones: 'Conversaciones', cuenta: 'Cuenta' };
const ACCION_LABEL = { crear: 'Alta', pausar: 'Pausa', reactivar: 'Reactivación', baja: 'Baja', reenviar_invitacion: 'Reenvío de invitación', consultar: 'Consulta' };
const ESTADO_APP = {
  sin_invitar: ['Sin invitar', 'var(--au-text-faint)'], invitado: ['Invitado', 'var(--au-yellow-fg)'], activo: ['Activo', 'var(--au-success-fg)'],
  pausado: ['En pausa', COLOR_PAUSA], baja: ['Baja', COLOR_BAJA], inactivo: ['Inactivo (antiguo)', 'var(--au-text-faint)'], // B52-B
};
const ESTADO_KOMITO = { invitado: 'var(--au-yellow-fg)', activo: 'var(--au-success-fg)', pausado: 'var(--au-orange-fg)', baja: 'var(--au-error-fg)', no_existe: 'var(--au-text-faint)' }; // APP 2/20 Fase A: tokens

// Estado del lado app con los datos del agente (lo mismo que responde `consultar` en el servidor).
// Bloque 52-B: el estado sale de baja_at / pausa_at (activo ya no se toca). Los activo=false de antes: «Inactivo (antiguo)».
export const estadoAppDe = (a) => {
  if (!a) return 'sin_invitar';
  if (a.baja_at) return 'baja';
  if (a.pausa_at) return 'pausado';
  if (a.activo === false) return 'inactivo';
  if (a.contrasena_creada_en) return 'activo';
  if (a.auth_uid) return 'invitado';
  return 'sin_invitar';
};

const fmtFecha = (f) => {
  if (!f) return '—';
  try { return new Date(f).toLocaleString('es-MX', { timeZone: 'America/Cancun', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return String(f); }
};

const Chip = ({ txt, color, title }) => (
  <span title={title || ''} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: RADIO_INSIGNIA, fontSize: 12, fontWeight: 800, letterSpacing: '.04em', color, background: `color-mix(in srgb, ${color} 14%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 40%, transparent)`, whiteSpace: 'nowrap' }}>{txt}</span>
);

// Una línea por lado con su palomita, sus pasos y «Reintentar» solo para el lado que falló.
export function ResultadoAlta({ C, res, accion, onReintentar, busy = false }) {
  if (!res) return null;
  const rojo = C.red; // APP 2/20 Fase A
  // Rechazo completo del servidor (NO_PERMITIDO, LIGADO_ADMIN, CORREO_INVALIDO, YA_DE_BAJA, FANTASMA…): ningún lado se tocó.
  if (!res.app && !res.komito) {
    return (
      <div className="au-banner au-banner--error" style={{ fontSize: 12 }}>
        <strong>{res.error || 'Error'}</strong> · {res.mensaje || 'No se pudo completar.'}
      </div>
    );
  }
  const amarillo = C.yellow;
  const filaLado = (lado, ok, titulo, cuerpo, espera = false) => (
    <div style={{ border: `1px solid ${espera ? `color-mix(in srgb, ${amarillo} 50%, transparent)` : ok ? 'color-mix(in srgb, var(--au-success-fg) 35%, transparent)' : `color-mix(in srgb, ${rojo} 45%, transparent)`}`, borderRadius: RADIO, padding: '10px 12px', marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: espera ? amarillo : ok ? C.green : rojo }}>{espera ? '⏳' : ok ? '✓' : '✗'} {titulo}</div>
        {!ok && !espera && onReintentar && <button type="button" className="au-btn au-btn--secondary" disabled={busy} onClick={() => onReintentar(lado)} style={{ height: 30, padding: '0 10px', fontSize: 12 }}>{busy ? 'Reintentando…' : `↻ Reintentar ${lado === 'app' ? 'la app' : 'Komito'}`}</button>}
      </div>
      <div style={{ fontSize: 12, color: espera ? C.text : ok ? C.text : rojo, marginTop: 4 }}>{espera ? <Chip txt={MSG_EN_PROCESO} color={amarillo} /> : cuerpo}{espera && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>En 10 s se consulta solo su estado.</div>}</div>
    </div>
  );
  const app = res.app; const k = res.komito;
  let cuerpoApp = null;
  if (app) {
    if (!app.ok) cuerpoApp = app.detalle || 'Falló el lado de la app.';
    else if (accion === 'consultar') cuerpoApp = `Estado: ${(ESTADO_APP[app.estado] || [app.estado])[0]}`;
    else cuerpoApp = MSG_APP[app.detalle] || app.detalle || (ESTADO_APP[app.estado] || [app.estado])[0];
  }
  let cuerpoK = null;
  if (k) {
    if (!k.ok) {
      const pasoMal = k.pasos ? Object.entries(k.pasos).filter(([, v]) => v === 'error').map(([p]) => PASO_LABEL[p] || p) : [];
      cuerpoK = `${MSG_KOMITO[k.error] || k.mensaje || k.error || 'Falló el lado de Komito.'}${pasoMal.length ? ` · Falló: ${pasoMal.join(', ')}` : ''}${k.error ? ` (${k.error})` : ''}`;
    } else if (k.pasos && typeof k.pasos === 'object') {
      cuerpoK = (
        <span style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {Object.entries(k.pasos).map(([p, v]) => {
            const mal = v === 'error'; const omit = v === 'omitido';
            return <span key={p} style={{ color: mal ? rojo : omit ? C.muted : C.text }}>{mal ? '✗' : omit ? '–' : '✓'} {PASO_LABEL[p] || p}: <strong>{String(v)}</strong></span>;
          })}
          {k.estado && <span style={{ color: C.muted }}>· estado <strong style={{ color: C.text }}>{k.estado}</strong></span>}
        </span>
      );
    } else {
      cuerpoK = `Estado: ${k.estado || '—'}${k.contrasena_creada != null ? ` · contraseña ${k.contrasena_creada ? 'creada' : 'pendiente'}` : ''}${k.conversaciones_abiertas != null ? ` · ${k.conversaciones_abiertas} conversaciones abiertas` : ''}`;
    }
  }
  return (
    <div>
      {app && filaLado('app', app.ok, 'App', cuerpoApp)}
      {k && filaLado('komito', k.ok, 'Komito', cuerpoK, komitoEnProceso(res))}
    </div>
  );
}

const COLS = 'id,nombre,tipo_agente,email_publico,activo,auth_uid,es_fantasma,contrasena_creada_en,invitacion_enviada_en,baja_at,baja_por,baja_motivo,pausa_at,pausa_por,pausa_motivo,komito_estado,komito_actualizado_en';
const COLS_BITACORA = 'id,solicitud_id,solicitante,agente_id,agente_nombre,equipo,accion,motivo,komito_ok,komito_estado,komito_pasos,komito_error,app_ok,app_estado,app_detalle,creado_en';

const Modal = ({ C, busy, onClose, titulo, children, ancho = 520 }) => createPortal(
  <div onClick={() => !busy && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 520, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    <div onClick={(e) => e.stopPropagation()} className="au-glass" style={{ background: C.card || C.surface, width: '100%', maxWidth: ancho, maxHeight: '88vh', overflowY: 'auto', padding: 20, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{titulo}</div>
        <button type="button" onClick={onClose} disabled={busy} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>✕</button>
      </div>
      {children}
    </div>
  </div>,
  document.body
);

export default function EquipoAgentes({ C, rol, equipoInicial = 'top_tours', onNuevo = null, refrescar = 0, onCambio = null }) {
  const equipos = equiposDeRol(rol);
  const esSuper = rol === 'super_admin';
  const [filtro, setFiltro] = useState(esSuper ? (equipos.includes(equipoInicial) ? equipoInicial : 'todos') : equipos[0]);
  const [agentes, setAgentes] = useState([]);
  const [bitacora, setBitacora] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(null); // agente_id con una llamada en curso
  const [modal, setModal] = useState(null);     // confirmar | resultado | historial
  const [progreso, setProgreso] = useState(null); // { hecho, total, nombre } mientras «Actualizar estados»
  const cancelarRef = useRef(false);
  const [enProceso, setEnProceso] = useState({}); // agente_id → true mientras Komito responde EN_PROCESO
  const timersRef = useRef([]);
  const vigilarEnProceso = (ag, accion) => {
    setEnProceso((x) => ({ ...x, [ag.id]: true }));
    const t = setTimeout(async () => {
      const c = await llamarAltaAgente({ accion: 'consultar', agente_id: ag.id });
      setEnProceso((x) => { const y = { ...x }; delete y[ag.id]; return y; });
      if (c?.komito?.estado) setAgentes((xs) => xs.map((z) => (z.id === ag.id ? { ...z, komito_estado: c.komito.estado, komito_actualizado_en: new Date().toISOString() } : z)));
      setModal((m) => (m && m.tipo === 'resultado' && m.ag.id === ag.id && komitoEnProceso(m.res) ? { ...m, res: { ...m.res, komito: { ...m.res.komito, ...(c?.komito || {}), ...(c?.komito?.ok ? { ok: true, error: null, mensaje: null } : {}) } }, consultaTras: true } : m));
      cargar();
    }, EN_PROCESO_MS);
    timersRef.current.push(t);
  };

  const cargar = useCallback(async () => {
    if (!equipos.length) { setCargando(false); return; }
    const [a, b] = await Promise.all([
      supabase.from('agentes').select(COLS).in('tipo_agente', equipos).order('nombre'),
      supabase.from('altas_bitacora').select(COLS_BITACORA).order('creado_en', { ascending: false }).limit(500),
    ]);
    if (a.error) { setError(`No se pudo leer agentes: ${a.error.message}`); setCargando(false); return; }
    setError(b.error ? `No se pudo leer la bitácora: ${b.error.message}` : '');
    setAgentes((a.data || []).filter((x) => !x.es_fantasma && equipos.includes(x.tipo_agente))); // sin fantasmas, ventas_admin ni agencias
    setBitacora(b.data || []);
    setCargando(false);
  }, [equipos.join(',')]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar, refrescar]);
  useEffect(() => () => { cancelarRef.current = true; timersRef.current.forEach(clearTimeout); }, []);

  if (!ROLES_EQUIPO.includes(rol)) return null;

  const visibles = agentes.filter((a) => filtro === 'todos' || a.tipo_agente === filtro);
  const ultimaDe = (id) => bitacora.find((f) => f.agente_id === id && f.accion !== 'consultar') || bitacora.find((f) => f.agente_id === id);

  // ── Acciones ──
  const ejecutar = async (ag, accion, { motivo = null, solo = null } = {}) => {
    setOcupado(ag.id);
    const res = await llamarAltaAgente({ accion, agente_id: ag.id, motivo, solo });
    setOcupado(null);
    setModal({ tipo: 'resultado', ag, accion, motivo, res });
    if (komitoEnProceso(res)) vigilarEnProceso(ag, accion);
    await cargar();
    if (onCambio) onCambio(); // las tarjetas de abajo (activo/pausa) también se recargan
  };
  const pedirConfirmacion = async (ag, accion) => {
    setOcupado(ag.id);
    const consulta = await llamarAltaAgente({ accion: 'consultar', agente_id: ag.id });
    setOcupado(null);
    if (!consulta?.app && !consulta?.komito) { setModal({ tipo: 'resultado', ag, accion: 'consultar', res: consulta }); return; }
    setModal({ tipo: 'confirmar', ag, accion, consulta, motivo: '' });
    cargar();
  };
  const actualizarEstados = async () => {
    const lista = visibles.filter((a) => correoC100(a.email_publico)); // sin correo @cancun100.com el servidor los rechaza: no gastan cupo
    if (!lista.length) return;
    cancelarRef.current = false;
    for (let i = 0; i < lista.length; i++) {
      if (cancelarRef.current) break;
      const ag = lista[i];
      setProgreso({ hecho: i, total: lista.length, nombre: ag.nombre });
      const r = await llamarAltaAgente({ accion: 'consultar', agente_id: ag.id });
      if (r?.komito?.estado) setAgentes((xs) => xs.map((x) => (x.id === ag.id ? { ...x, komito_estado: r.komito.estado, komito_actualizado_en: new Date().toISOString() } : x)));
      if (komitoEnProceso(r)) vigilarEnProceso(ag, 'consultar');
      if (i < lista.length - 1 && !cancelarRef.current) await new Promise((ok) => setTimeout(ok, PAUSA_REFRESCO_MS));
    }
    setProgreso(null);
    await cargar();
  };
  const abrirHistorial = (ag) => setModal({ tipo: 'historial', ag });

  // ── Estilos ──
  const card = { background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, backdropFilter: 'blur(var(--au-glass-blur))', WebkitBackdropFilter: 'blur(var(--au-glass-blur))', boxShadow: 'var(--au-shadow-card)', padding: 16, marginBottom: 14 };
  const btn = (extra) => ({ height: 30, boxSizing: 'border-box', padding: '0 10px', fontSize: 12, ...extra });
  const btnF = (apagado, extra) => btn({ ...extra, ...(apagado ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }); // B52: que se vea apagado
  const input = { width: '100%', background: C.dark, border: `1px solid ${C.border}`, borderRadius: RADIO, boxSizing: 'border-box', padding: '10px 12px', color: C.text, fontSize: 14, fontFamily: 'var(--au-font-text)', outline: 'none' };

  const renderModal = () => {
    if (!modal) return null;
    const m = modal;
    const cerrar = () => setModal(null);
    if (m.tipo === 'confirmar') {
      const n = m.consulta?.komito?.conversaciones_abiertas;
      const errK = m.consulta?.komito && !m.consulta.komito.ok ? (MSG_KOMITO[m.consulta.komito.error] || m.consulta.komito.mensaje || m.consulta.komito.error) : '';
      const esBaja = m.accion === 'baja';
      return (
        <Modal C={C} busy={ocupado != null} onClose={cerrar} titulo={`${esBaja ? '⛔ Baja' : '⏸ Pausar'} · ${m.ag.nombre}`}>
          <div className="au-banner au-banner--error" style={{ fontSize: 14, marginBottom: 12, lineHeight: 1.5 }}>
            {/* Textos exactos del punto 3; N = conversaciones_abiertas de consultar. Un solo bloque: .au-banner es flex */}
            <div>No podrá entrar a la app ni a Komito.{' '}
            {n != null
              ? (esBaja
                ? <>Sus <strong>{n}</strong> conversaciones abiertas quedan libres para que otro agente las tome.</>
                : <>Sus <strong>{n}</strong> conversaciones abiertas se quedan a su nombre.</>)
              : <>No se pudo leer cuántas conversaciones abiertas tiene en Komito{errK ? ` (${errK})` : ''}; {esBaja ? 'las que tenga quedan libres para que otro agente las tome' : 'las que tenga se quedan a su nombre'}.</>}
            {' '}No se borra nada.</div>
          </div>
          <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: 'block', marginBottom: 6 }}>Motivo (opcional)</label>
          <textarea rows={2} maxLength={300} style={{ ...input, resize: 'vertical', marginBottom: 4 }} value={m.motivo} onChange={(e) => setModal({ ...m, motivo: e.target.value })} />
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={cerrar} disabled={ocupado != null}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" disabled={ocupado != null} style={{ background: C.red }}
              onClick={() => ejecutar(m.ag, m.accion, { motivo: String(m.motivo || '').trim() || null })}>
              {ocupado != null ? 'Procesando…' : esBaja ? '⛔ Dar de baja' : '⏸ Pausar'}
            </button>
          </div>
        </Modal>
      );
    }
    if (m.tipo === 'resultado') {
      return (
        <Modal C={C} busy={ocupado != null} onClose={cerrar} titulo={`${ACCION_LABEL[m.accion] || m.accion} · ${m.ag.nombre}`}>
          <ResultadoAlta C={C} res={m.res} accion={m.accion} busy={ocupado != null}
            onReintentar={(lado) => ejecutar(m.ag, m.accion, { motivo: m.motivo || null, solo: lado })} />
          {(m.accion === 'crear' || m.accion === 'reenviar_invitacion') && (m.res?.app || m.res?.komito) && <div className="au-banner" style={{ fontSize: 12, marginTop: 6 }}>📧 {AVISO_DOS_CORREOS}</div>}
          <div className="au-pie"><button type="button" className="au-btn au-btn--primary" onClick={cerrar} disabled={ocupado != null}>Listo</button></div>
        </Modal>
      );
    }
    if (m.tipo === 'historial') {
      const filas = bitacora.filter((f) => f.agente_id === m.ag.id);
      const lado = (ok, estado, extra) => (ok == null ? <span style={{ color: C.muted }}>—</span>
        : <span style={{ color: ok ? C.green : C.red, fontWeight: 700 }}>{ok ? '✓' : '✗'} {estado || ''}{extra ? <span style={{ fontWeight: 400, color: C.muted }}> · {extra}</span> : null}</span>);
      return (
        <Modal C={C} busy={false} onClose={cerrar} titulo={`Historial de altas · ${m.ag.nombre}`} ancho={720}>
          {filas.length === 0 ? <div style={{ color: C.muted, padding: 20, textAlign: 'center' }}>Sin movimientos en la bitácora.</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {['Fecha', 'Quién', 'Acción', 'Motivo', 'App', 'Komito'].map((h) => <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, whiteSpace: 'nowrap' }}>{h}</th>)}
                </tr></thead>
                <tbody>{filas.map((f) => (
                  <tr key={f.id} style={{ borderBottom: `1px solid ${C.border}`, verticalAlign: 'top' }}>
                    <td style={{ padding: '6px 8px', color: C.muted, whiteSpace: 'nowrap' }}>{fmtFecha(f.creado_en)}</td>
                    <td style={{ padding: '6px 8px', color: C.text }}>{f.solicitante || '—'}</td>
                    <td style={{ padding: '6px 8px', color: C.text, fontWeight: 600 }}>{ACCION_LABEL[f.accion] || f.accion}</td>
                    <td style={{ padding: '6px 8px', color: C.muted }}>{f.motivo || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{lado(f.app_ok, f.app_estado, f.app_ok === false ? f.app_detalle : (MSG_APP[f.app_detalle] || null))}</td>
                    <td style={{ padding: '6px 8px' }}>{lado(f.komito_ok, f.komito_estado, f.komito_ok === false ? (MSG_KOMITO[f.komito_error] || f.komito_error) : null)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </Modal>
      );
    }
    return null;
  };

  const fila = (a) => {
    const est = estadoAppDe(a);
    const [estTxt, estCol] = ESTADO_APP[est] || [est, C.muted];
    const kEst = a.komito_estado;
    const ult = ultimaDe(a.id);
    const malCorreo = !correoC100(a.email_publico);
    const enCurso = ocupado === a.id;
    const bloqueado = ocupado != null || !!progreso || malCorreo;
    const tituloBloq = malCorreo ? 'El correo debe ser @cancun100.com: edítalo en su tarjeta (abajo) antes de usar Komito.' : '';
    const deBaja = !!a.baja_at;
    const enPausa = !!a.pausa_at; // B52-B
    return (
      <div key={a.id} className="b52-fila" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <button type="button" onClick={() => abrirHistorial(a)} title="Ver su historial de altas" style={{ background: 'none', border: 'none', padding: 0, color: C.text, fontWeight: 700, fontSize: 14, cursor: 'pointer', textAlign: 'left' }}>{a.nombre}</button>
          <div style={{ fontSize: 12, color: malCorreo ? C.red : C.muted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.email_publico || 'sin correo'}{malCorreo && ' · no es @cancun100.com'}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{NOMBRE_EQUIPO_B52[a.tipo_agente]}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', flex: '1 1 200px' }}>
          <span style={{ fontSize: 12, color: C.muted }}>App</span>
          <Chip txt={estTxt} color={estCol} title={est === 'inactivo' ? 'Dado de baja con el sistema anterior (activo = false)' : (a.pausa_motivo || a.baja_motivo || '')} />
          <span style={{ fontSize: 12, color: C.muted, marginLeft: 4 }}>Komito</span>
          {enProceso[a.id]
            ? <Chip txt={MSG_EN_PROCESO} color={C.yellow} title="Se consulta solo en 10 s" />
            : <Chip txt={kEst || 'sin dato'} color={kEst ? (ESTADO_KOMITO[kEst] || C.blue) : 'var(--au-text-faint)'} title={a.komito_actualizado_en ? `al ${fmtFecha(a.komito_actualizado_en)}` : 'Toca «Actualizar estados» para leerlo'} />}
        </div>
        <div style={{ flex: '1 1 160px', fontSize: 12, color: C.muted }}>
          {ult ? <>{ACCION_LABEL[ult.accion] || ult.accion} · {fmtFecha(ult.creado_en)}{(ult.app_ok === false || ult.komito_ok === false) && <span style={{ color: C.red, fontWeight: 700 }}> · con error</span>}</> : 'Sin acciones'}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', flex: '1 1 260px' }} title={tituloBloq}>
          {enCurso && <span style={{ fontSize: 12, color: C.blue, alignSelf: 'center' }}>⏳ Procesando…</span>}
          {!deBaja && !enPausa && <button type="button" className="au-btn au-btn--ghost" style={btnF(bloqueado, { color: 'var(--au-orange-fg)' })} disabled={bloqueado} onClick={() => pedirConfirmacion(a, 'pausar')}>⏸ Pausar</button>}
          {!deBaja && enPausa && <button type="button" className="au-btn au-btn--secondary" style={btnF(bloqueado, { color: C.green })} disabled={bloqueado} onClick={() => ejecutar(a, 'reactivar')}>▶ Reactivar</button>}
          {!deBaja && !enPausa && (!a.contrasena_creada_en || kEst !== 'activo') && <button type="button" className="au-btn au-btn--secondary" style={btnF(bloqueado)} disabled={bloqueado} onClick={() => ejecutar(a, 'reenviar_invitacion')}>✉️ Reenviar invitación</button>}
          {esSuper && !deBaja && <button type="button" className="au-btn au-btn--ghost" style={btnF(bloqueado, { color: C.red })} disabled={bloqueado} onClick={() => pedirConfirmacion(a, 'baja')}>⛔ Baja</button>}
          {deBaja && <span style={{ fontSize: 12, color: C.muted, alignSelf: 'center' }}>Dado de baja{a.baja_por ? ` por ${a.baja_por}` : ''}</span>}
          {deBaja && <button type="button" className="au-btn au-btn--ghost" style={btn()} onClick={() => abrirHistorial(a)}>Ver historial</button>}
        </div>
      </div>
    );
  };

  return (
    <div style={{ ...card, marginTop: 20 }} data-seccion="equipo-agentes">
      {renderModal()}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>🪪 Equipo · Agentes <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>app + Komito</span></div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Alta, pausa, reactivación{esSuper ? ', baja' : ''} e invitaciones en la app y en Komito con un solo botón. El correo de Titan se sigue creando a mano.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {esSuper && (
            <div className="au-toggle">
              {[['todos', 'Todos'], ['top_tours', 'Top Tours'], ['sala', 'Sala']].map(([k, l]) => <button key={k} type="button" aria-pressed={filtro === k} onClick={() => setFiltro(k)}>{l}</button>)}
            </div>
          )}
          {progreso
            ? <button type="button" className="au-btn au-btn--ghost" onClick={() => { cancelarRef.current = true; }}>⏹ Detener ({progreso.hecho + 1}/{progreso.total})</button>
            : <button type="button" className="au-btn au-btn--secondary" disabled={ocupado != null || !visibles.length} onClick={actualizarEstados} title="Consulta a cada agente visible, uno por uno con 7 s de pausa (Komito permite 10 por minuto)">↻ Actualizar estados</button>}
          {onNuevo && <button type="button" className="au-btn au-btn--primary" disabled={!!progreso} onClick={() => onNuevo(filtro === 'todos' ? equipoInicial : filtro)}>+ Nuevo agente</button>}
        </div>
      </div>
      {progreso && <div style={{ fontSize: 12, color: C.blue, marginBottom: 8 }}>Consultando a {progreso.nombre} ({progreso.hecho + 1} de {progreso.total})… entre uno y otro se esperan 7 s.</div>}
      {error && <div className="au-banner au-banner--error" style={{ fontSize: 12, marginBottom: 8 }}>{error}</div>}
      {cargando ? <div style={{ color: C.muted, padding: 16 }}>Cargando…</div>
        : visibles.length === 0 ? <div style={{ color: C.muted, padding: 16, textAlign: 'center', fontSize: 12 }}>Sin agentes en este equipo.</div>
        : <div>{visibles.map(fila)}</div>}
    </div>
  );
}
